/* eslint-disable @typescript-eslint/no-unused-vars-experimental */
import type { ActionValues } from 'components/move-list/state-machine/types';
import { Actions, States } from 'components/move-list/state-machine/types';
import type { MoveListComponent } from 'components/move-list/config/types';
import type { MachineService } from 'src/utils/state-machine';
import { createMachineService } from 'src/utils/state-machine';
import type { StoreActions } from '../store/types';

/**
 * Create the move-list state machine
 */
export function createMoveListStateMachine(
  context: MoveListComponent.Context,
): MachineService<ActionValues> {
  const moveListService = createMachineService<ActionValues>(getStateMachine(context));

  moveListService.onTransition((ev) => [renderHandler].forEach((effect) => effect(ev, context)));

  return moveListService;
}

/**
 * Handle UI rendering from state machine events
 */
function renderHandler({ type, value, nextState }, context) {
  const oldState = context.data.currentState.get();
  const {
    setMoveListRows,
    selectNode,
    setResult,
    setState,
    setGameMetadataFromGameInfo,
    setContextMenu,
    setVariationPopover,
    setStartingMoveNumber,
  } = <StoreActions>context.actions;

  if (oldState !== nextState) {
    setState(nextState);
  }

  const actions = {
    [Actions.LOAD_LINE]: ({ lines, result, node }) => {
      const { updatedLayout } = selectNode(node, { lines });
      if (!updatedLayout) {
        setMoveListRows(lines);
      }
      setResult(result);
      setStartingMoveNumber(context.game.getStartingMoveNumber());
      setGameMetadataFromGameInfo(context.game);
      setContextMenu({ isOpen: false });
      setVariationPopover({ isOpen: false });
    },
    [Actions.SELECT_NODE]: ({ node, disableScrollIfScrolledUp }) => {
      selectNode(node, { disableScrollIfScrolledUp });
      setContextMenu({ isOpen: false });
      setVariationPopover({ isOpen: false });
    },
    [Actions.DESELECT_NODE]: () => {
      selectNode(null);
    },
  };

  actions[type]?.(value, nextState);
}

/**
 * Get the state machine for the move list
 */
function getStateMachine({ game, data }: MoveListComponent.Context) {
  return {
    initialState: States.NoNodeSelected,
    states: {
      [States.NoNodeSelected]: {
        SELECT_NODE: () => States.NodeSelected,
        LOAD_LINE: () => States.NodeSelected,
      },
      [States.NodeSelected]: {
        SELECT_NODE: () => States.NodeSelected,
        DESELECT_NODE: () => States.NoNodeSelected,
        LOAD_LINE: () => States.NodeSelected,
      },
    },
  };
}
